import React, {memo, useEffect, useState} from "react"
import {useFormik} from "formik"
import * as yup from "yup"
import moment from "moment/moment"
import {FormSelect} from "../FormSelect"
import {FormInput} from "../FormInput"
import {FormTextarea} from "../FormTextarea"
import {AttributeRuleType} from "../../../types/types"

type PropsTypes = {
    rules?: string
    attributes: string
    setAttributes: (value: string) => void
    setSubmitCallbacks: any
}
export const JsonFormValues: React.FC<PropsTypes> = memo(({rules, attributes, setAttributes, setSubmitCallbacks}) => {
    const [schema, setSchema] = useState(yup.object().shape({}))
    const [formFields, setFormFields] = useState([])

    const formik = useFormik({
        initialValues: {} as any,
        validationSchema: schema,
        onSubmit: () => {
        },
    })
    const {values, handleChange, handleBlur, errors, touched, setFieldValue, validateForm, setFieldTouched, setValues} = formik

    useEffect(() => {
        setSubmitCallbacks(() => async () => {
            for (const key in values) {
                await setFieldTouched(key, true)
            }
            const formValid = await validateForm(values)
            return Object.keys(formValid).length === 0 && formValid.constructor === Object
        })
    }, [values])

    useEffect(() => {
        const json: Array<object> = []

        for (const key in values) {
            if (key.includes('_alias')) {
                const alias = values[key]
                const item = {
                    alias: alias,
                    value: values[`${alias}_value`],
                    created_at: values[`${alias}_created_at`] || moment.utc(),
                    updated_at: moment.utc(),
                }

                if (item) json.push(item)
            }

            if (values[key] === undefined) delete values[key]
        }

        setAttributes(JSON.stringify(json))
    }, [values])

    useEffect(() => {
        if (attributes) {
            const parsed = JSON.parse(attributes)
            if (Array.isArray(parsed)) {
                parsed?.forEach((i: any) => {
                    setFieldValue(`${i.alias}_alias`, i.alias)
                    setFieldValue(`${i.alias}_value`, i.value)
                    setFieldValue(`${i.alias}_created_at`, i.created_at)
                    setFieldValue(`${i.alias}_updated_at`, i.updated_at)
                })
            }
        }
    }, [attributes])

    useEffect(() => {
        if (rules) {
            const parsedRules = JSON.parse(rules)
            const schemaObject: any = {}
            const forms: any = []
            const aliases: Array<string> = []
            const valuesKeys = Object.keys(values)

            if (Array.isArray(parsedRules)) {
                parsedRules?.sort((a: AttributeRuleType, b: AttributeRuleType) => a.pos - b.pos)?.forEach((i: AttributeRuleType) => {
                    if (i.is_deleted === false) {
                        aliases.push(i.alias)

                        switch (i.type) {
                            case 'text':
                                schemaObject[`${i.alias}_value`] = yup.string().max(i.length, `Максимальная длина ${i.length}`)
                                if (i.required) schemaObject[`${i.alias}_value`] = yup.string().max(i.length, `Максимальная длина ${i.length}`).required('Обязательное поле')

                                if (values[`${i.alias}_alias`] === undefined || values[`${i.alias}_value`] === undefined) {
                                    setFieldValue(`${i.alias}_alias`, i.alias)
                                    setFieldValue(`${i.alias}_value`, '')
                                }

                                forms.push((
                                    <FormInput placeholder={i.name}
                                               value={values[`${i.alias}_value`]}
                                               name={`${i.alias}_value`}
                                               onBlur={handleBlur}
                                               onChange={handleChange}
                                               isInvalid={touched[`${i.alias}_value`] && errors[`${i.alias}_value`]}
                                               allowClear
                                               key={`${i.alias}_value`}
                                    />
                                ))
                                break
                            case 'textarea':
                                schemaObject[`${i.alias}_value`] = yup.string().max(i.length, `Максимальная длина ${i.length}`)
                                if (i.required) schemaObject[`${i.alias}_value`] = yup.string().max(i.length, `Максимальная длина ${i.length}`).required('Обязательное поле')

                                if (values[`${i.alias}_alias`] === undefined || values[`${i.alias}_value`] === undefined) {
                                    setFieldValue(`${i.alias}_alias`, i.alias)
                                    setFieldValue(`${i.alias}_value`, '')
                                }

                                forms.push((
                                    <FormTextarea placeholder={i.name}
                                                  value={values[`${i.alias}_value`]}
                                                  name={`${i.alias}_value`}
                                                  onBlur={handleBlur}
                                                  onChange={handleChange}
                                                  isInvalid={touched[`${i.alias}_value`] && errors[`${i.alias}_value`]}
                                                  allowClear
                                                  autoSize={{minRows: 2}}
                                                  key={`${i.alias}_value`}
                                    />
                                ))
                                break
                            case 'number':
                                schemaObject[`${i.alias}_value`] = yup.number().typeError('Это не цифры').max(i.length, `Максимальная длина ${i.length}`)
                                if (i.required) schemaObject[`${i.alias}_value`] = yup.number().typeError('Это не цифры').max(i.length, `Максимальная длина ${i.length}`).required('Обязательное поле')

                                if (values[`${i.alias}_alias`] === undefined || values[`${i.alias}_value`] === undefined) {
                                    setFieldValue(`${i.alias}_alias`, i.alias)
                                    setFieldValue(`${i.alias}_value`, '')
                                }

                                forms.push((
                                    <FormInput placeholder={i.name}
                                               value={values[`${i.alias}_value`]}
                                               type="number"
                                               name={`${i.alias}_value`}
                                               onBlur={handleBlur}
                                               onChange={handleChange}
                                               isInvalid={touched[`${i.alias}_value`] && errors[`${i.alias}_value`]}
                                               allowClear
                                               key={`${i.alias}_value`}
                                    />
                                ))
                                break
                            case 'radio':
                                if (i.required) schemaObject[`${i.alias}_value`] = yup.string().typeError('Выберите опцию').required('Выберите опцию')

                                if (values[`${i.alias}_alias`] === undefined || values[`${i.alias}_value`] === undefined) {
                                    setFieldValue(`${i.alias}_alias`, i.alias)
                                    setFieldValue(`${i.alias}_value`, null)
                                }

                                forms.push((
                                    <FormSelect placeholder={i.name}
                                                id={`${i.alias}_value`}
                                                value={values[`${i.alias}_value`]}
                                                onChange={(value) => setFieldValue(`${i.alias}_value`, value)}
                                                onBlur={handleBlur}
                                                options={i.options}
                                                className="form-select"
                                                allowClear
                                                isInvalid={touched[`${i.alias}_value`] && errors[`${i.alias}_value`]}
                                                key={`${i.alias}_value`}
                                    />
                                ))
                                break
                            case 'checkboxes':
                                if (i.required) schemaObject[`${i.alias}_value`] = yup.array().min(1, 'Выберите опции')
                                    .of(yup.string().required('Выберите опции')).required('Выберите опции').required('Выберите опции')

                                if (values[`${i.alias}_alias`] === undefined || values[`${i.alias}_value`] === undefined) {
                                    setFieldValue(`${i.alias}_alias`, i.alias)
                                    setFieldValue(`${i.alias}_value`, [])
                                }

                                forms.push((
                                    <FormSelect placeholder={i.name}
                                                id={`${i.alias}_value`}
                                                value={values[`${i.alias}_value`]}
                                                onChange={(values) => setFieldValue(`${i.alias}_value`, values)}
                                                onBlur={handleBlur}
                                                options={i.options}
                                                className="form-select"
                                                allowClear
                                                isInvalid={touched[`${i.alias}_value`] && errors[`${i.alias}_value`]}
                                                key={`${i.alias}_value`}
                                                mode="multiple"
                                    />
                                ))
                                break
                        }
                    }
                })
            }

            if (Array.isArray(valuesKeys)) {
                valuesKeys?.forEach(i => {
                    let needed = false
                    aliases.forEach(a => {
                        if (`${a}_alias` === i || `${a}_value` === i || `${a}_created_at` === i || `${a}_updated_at` === i) needed = true
                    })
                    if (!needed) delete values[i]
                })
            }

            setFormFields(forms)
            setSchema(yup.object().shape(schemaObject))
        }
    }, [rules, values, touched, errors])

    return (
        <>
            {formFields}
        </>
    )
})
